'use client';

import { useRouter } from 'next/navigation';

import { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';

import { Card, Dropdown, DropdownItem, Pagination, Spinner } from '@uikit';

import type { CategoryApp, StoreSearchAppsSearchParams, SubCategoryApp } from '@api';

import AngleDown from '@uikit/icons/AngleDown';
import Close from '@uikit/icons/Close';

import { useSearchAppsQuery } from '@shared/hooks/query/search';

import { useCategoriesQuery } from '@query/apps';

import SearchCard from '@store:web/components/common/AppCard/SearchCard';
import HeaderSearchBar from '@store:web/components/layout/Header/HeaderSearchBar';

const limitsArray = [30, 60, 90];

interface SearchPageProps {
  searchParams?: StoreSearchAppsSearchParams;
}

const SearchPage = ({ searchParams: initialSearchParams }: SearchPageProps) => {
  const router = useRouter();
  const find = initialSearchParams?.find || '';
  const [tags, setTags] = useState(initialSearchParams?.tags || '');
  const orderParams = initialSearchParams?.order || 'release_date';
  const [limit, setLimit] = useState(Number(initialSearchParams?.limit) || 30);
  const [selectedCat, setSelectedCat] = useState(Number(initialSearchParams?.category) || 0);
  const [selectedSubCat, setSelectedSubCat] = useState(
    Number(initialSearchParams?.subcategory) || 0,
  );
  const [order, setOrder] = useState(orderParams);
  const {
    data: apps,
    isLoading,
    pagination,
  } = useSearchAppsQuery({
    find,
    limit,
    tags,
    category: selectedCat,
    subcategory: selectedSubCat,
    ...(order !== 'release_date' && { order }),
  });
  const { data: categories, isPending: isLoadingCategory } = useCategoriesQuery();
  const subCategories = useMemo(() => {
    if (!selectedCat) {
      return [];
    }

    return categories?.find((cat) => cat.category_id === selectedCat)?.subcategory || [];
  }, [selectedCat, categories]);
  const selectedCategoryObj = useMemo(() => {
    if (selectedCat === 0) {
      return {
        category_id: 0,
        category_name: 'All categories',
        g_id: 0,
        google_category_name: '',
        subcategory: [],
      };
    }

    return categories?.find((cat) => cat.category_id === selectedCat);
  }, [selectedCat, categories]);
  const selectedSubCategoryObj = useMemo(() => {
    if (!selectedSubCat) {
      return {
        subcategory_id: 0,
        subcategory_name: 'All subcategories',
      };
    }

    return subCategories.find((subCat) => subCat.subcategory_id === selectedSubCat);
  }, [selectedSubCat, subCategories]);

  const handleLimit = (count: number) => {
    pagination.reset();
    setLimit(count);
  };

  const handleOrder = (selectedOrder: string) => {
    pagination.reset();
    setOrder(selectedOrder);
  };

  const handleSubCategory = (subCategory: SubCategoryApp) => {
    pagination.reset();
    setSelectedSubCat(subCategory.subcategory_id);
  };

  const handleCategory = (category: CategoryApp) => {
    pagination.reset();
    setSelectedCat(category.category_id);

    handleSubCategory({
      subcategory_name: 'All categories',
      subcategory_id: 0,
    });
  };

  useEffect(() => {
    const query = new URLSearchParams({
      ...(find ? { find } : {}),
      ...(tags ? { tags } : {}),
      ...(limit !== 30 ? { limit: String(limit) } : {}),
      ...(selectedCat !== 0 ? { category: String(selectedCat) } : {}),
      ...(selectedSubCat !== 0 ? { subcategory: String(selectedSubCat) } : {}),
      ...(order === 'ratings' ? { order } : {}),
      ...(pagination.currentPage !== 1
        ? { offset: String((pagination.currentPage - 1) * limit) }
        : {}),
    });
    router.push(`/search?${query.toString()}`);
  }, [limit, selectedCat, selectedSubCat, order, pagination.currentPage, router, find, tags]);

  return (
    <div className="v2-container store-container-pt relative z-[1]">
      <HeaderSearchBar className="lg:hidden mx-auto mb-6 z-popover" />

      <Card variant="bordered" className="p-8 bg-neutral-50 flex-grow">
        {(isLoading || isLoadingCategory) && <Spinner />}

        {!isLoadingCategory && (
          <div className="mb-9 flex flex-col xl:flex-row justify-between items-start">
            <div className="flex justify-start gap-6 items-center pb-3 xl:pb-0">
              <h1 className="t-h3">
                Search Result {find ? `for "${find}"` : ''}{' '}
                <span className="text-secondary">{apps?.total_apps}</span>
              </h1>
              {!!tags && (
                <div className="h-6 px-3 inline-flex items-center border rounded-full border-primary-200 hover:border-primary hover:bg-primary-100 transition-colors t-caption2 shadow-card text-neutral-600">
                  {tags}
                  <button className="ml-4 icon-xs">
                    <Close onClick={() => setTags('')} />
                  </button>
                </div>
              )}
            </div>
            <div className="flex flex-col items-start md:flex-row md:items-center justify-start gap-5">
              <div className="flex items-center justify-start gap-4">
                <p className="t-h4">Apps per page:</p>
                <Dropdown
                  trigger={
                    <button className="flex items-center justify-start focus-visible:outline-none">
                      {limit} <AngleDown className="icon text-secondary" />
                    </button>
                  }
                >
                  {limitsArray.map((el) => (
                    <DropdownItem
                      key={el}
                      onClick={() => {
                        handleLimit(el);
                      }}
                      label={String(el)}
                    />
                  ))}
                </Dropdown>
              </div>

              <div className="flex items-center flex-wrap justify-start gap-4">
                <p className="t-h4">Filter by:</p>
                <Dropdown
                  trigger={
                    <button className="flex items-center justify-start focus-visible:outline-none">
                      {selectedCategoryObj?.category_name}{' '}
                      <AngleDown className="icon text-secondary" />
                    </button>
                  }
                >
                  <DropdownItem
                    onClick={() => {
                      handleCategory({
                        category_name: 'All categories',
                        category_id: 0,
                        g_id: 0,
                        google_category_name: '',
                        subcategory: [],
                      });
                    }}
                    label="All categories"
                  />

                  {categories?.map((category) => (
                    <DropdownItem
                      key={category.category_id}
                      onClick={() => {
                        handleCategory(category);
                      }}
                      label={category.category_name}
                    />
                  ))}
                </Dropdown>
                {!!subCategories.length && (
                  <Dropdown
                    trigger={
                      <button className="flex items-center justify-start focus-visible:outline-none">
                        {selectedSubCategoryObj?.subcategory_name}{' '}
                        <AngleDown className="icon text-secondary" />
                      </button>
                    }
                  >
                    <DropdownItem
                      onClick={() => {
                        handleSubCategory({
                          subcategory_name: 'All categories',
                          subcategory_id: 0,
                        });
                      }}
                      label="All categories"
                    />
                    {subCategories?.map((subCat) => (
                      <DropdownItem
                        key={subCat.subcategory_id}
                        onClick={() => {
                          handleSubCategory(subCat);
                        }}
                        label={subCat.subcategory_name}
                      />
                    ))}
                  </Dropdown>
                )}
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    handleOrder('release_date');
                  }}
                  className={classNames(
                    'text-right text-neutral-400 font-semibold relative cursor-pointer capitalize text-xs',
                    {
                      'text-neutral-700 after:content-["."] after:text-[3.5rem] after:absolute after:left-[50%] after:top-0 after:text-secondary after:-bottom-6 after:-translate-x-1/2':
                        order === 'release_date',
                    },
                  )}
                >
                  New listed
                </button>
                <button
                  onClick={() => {
                    handleOrder('ratings');
                  }}
                  className={classNames(
                    'text-right text-neutral-400 font-semibold relative cursor-pointer capitalize text-xs',
                    {
                      'text-neutral-700 after:content-["."] after:text-[3.5rem] after:absolute after:left-[50%] after:top-0 after:text-secondary after:-bottom-6 after:-translate-x-1/2':
                        order === 'ratings',
                    },
                  )}
                >
                  Rating
                </button>
              </div>
            </div>
          </div>
        )}

        {!isLoading && !!apps?.data?.length && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {apps?.data.map((dataItem) => (
              <div key={dataItem.app_id} className="border-b border-neutral-100 pb-6">
                <SearchCard app={dataItem} />
              </div>
            ))}
          </div>
        )}
        {!isLoading && !apps?.data?.length && (
          <h2 className="t-h2 text-center py-20">There are no apps</h2>
        )}

        <hr className="my-14 h-px border-0 bg-primary-100 " />
        <div className="flex justify-center">
          <Pagination
            totalPages={pagination.totalPages}
            currentPage={pagination.currentPage}
            onPageChange={(selectedPage) => {
              pagination.goToPage(selectedPage - 1);
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default SearchPage;
